import React, { forwardRef } from 'react';
import { Link, useLocation, createPath } from 'react-router-dom';
import { getProjectIdFromPath } from '@/utils/route';

function isModifiedEvent(e: React.MouseEvent): boolean {
  return !!(e.metaKey || e.ctrlKey || e.altKey || e.shiftKey || e.button !== 0);
}

/**
 * SafeNavLink - Enhanced Link component with pre-navigation cleanup
 * 
 * When leaving ProjectDetail routes, pre-hides Execute artifacts before
 * letting the default <Link> handle navigation naturally.
 * Transparent to browser native behaviors (middle-click, Cmd+click, target="_blank", etc.)
 */
export const SafeNavLink = forwardRef<
  HTMLAnchorElement,
  React.ComponentProps<typeof Link>
>(function SafeNavLink({ to, onClick, ...rest }, ref) {
  const location = useLocation();
  
  const currentPathNoHash = location.pathname + location.search;
  const toPathNoHash = typeof to === 'string' 
    ? to.split('#')[0] 
    : (to.pathname || '') + (to.search || '');
  const onProjectDetail = !!getProjectIdFromPath(location.pathname);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Call original onClick if provided
    onClick?.(e);
    
    // Don't intercept if:
    if (e.defaultPrevented) return;
    if (isModifiedEvent(e)) return;
    if ((rest as any).target === '_blank') return;
    if ((rest as any).download) return;

    const toIsProjectDetail = !!getProjectIdFromPath(toPathNoHash);

    // Pre-hide Execute artifacts before navigation (cosmetic, non-blocking)
    if (onProjectDetail && !toIsProjectDetail && toPathNoHash !== currentPathNoHash) {
      if (import.meta.env.DEV) {
        console.time('nav:click→projects-mounted');
      }
      try {
        document
          .querySelectorAll('#execute-portal-root,[data-xc="execute-portal-root"]')
          .forEach((el) => el.remove());
        document.querySelectorAll('.execute-root').forEach((el) => {
          (el as HTMLElement).style.display = 'none';
          el.setAttribute('data-execute-hidden', 'true');
        });
      } catch {}
      // Let default <Link> handle navigation naturally
      // RouteTransitionCleanup handles query cleanup on route change
    }
  };

  return <Link ref={ref} to={to} onClick={handleClick} {...rest} />;
});
